import Button from "../Button";
import { t } from "i18next";
import React, { CSSProperties, FC, useCallback, useState } from "react";
import "./ContactForm.scss";
import { getCurrentLanguage } from "../../i18n";
import { Checkbox } from "../Checkbox";

interface UserType {
  id: number;
  key: string;
  callback?: Function;
}

type ContactFormProps = {
  onSuccess();
};

const ContactForm: FC<ContactFormProps> = ({ onSuccess }) => {
  const [selectedUserType, setSelectedUserType] = useState<UserType>(null);
  const [email, setEmail] = useState<string>(null);
  const [contactName, setContactName] = useState<string>(null);
  const [description, setDescription] = useState<string>(null);
  const [acceptPrivacyPolicy, setAcceptPrivacyPolicy] =
    useState<boolean>(false);
  const [acceptConditionsOfUse, setAcceptConditionsOfUse] =
    useState<boolean>(false);
  const [consentOfferServices, setConsentOfferServices] =
    useState<boolean>(false);
  const [consentThirdParty, setConsentThirdParty] = useState<boolean>(false);
  const lang = getCurrentLanguage();

  const userTypes: UserType[] = [
    { id: 1, key: "agency" },
    { id: 2, key: "brand" },
    { id: 3, key: "creator" },
    { id: 4, key: "careers" },
    {
      id: 5,
      key: "complaintsChannel",
      callback: () => window.open("https://youplanet.bizneohr.com/whistleblowing-channel", "_blank")
    },
  ];

  const textAreaStyles = {
    resize: "none",
  } as CSSProperties;

  const onSelectUserType = useCallback((userType: UserType) => {
    setSelectedUserType(userType);
    if (userType.callback) {
      userType.callback();
    }
  }, []);

  const delayTime = 0.1;

  const delayStyle = (delay: number) => {
    return { animationDelay: `${delay * delayTime}s` } as CSSProperties;
  };

  const radioButton = (userType: UserType, i: number) => {
    return (
      <label
        htmlFor="userType"
        className={`contact-form__radio-label cursor-click ${
          userType.id === selectedUserType?.id ? " is-active" : ""
        } is-animated`}
        style={delayStyle(i)}
        tabIndex={0}
        key={`radio-${userType.key}-${i}`}
        onClick={() => onSelectUserType(userType)}
      >
        <input
          className="contact-form__input-hidden"
          type="radio"
          value={userType.key}
          name="userType"
        />
        {t(`contact.form.userType.${userType.key}`)}
      </label>
    );
  };

  const onSubmit = (event: any) => {
    event.preventDefault();
    const data = {
      userType: selectedUserType.key,
      acceptPrivacyPolicy: acceptPrivacyPolicy,
      acceptConditionsOfUse: acceptConditionsOfUse,
      consentOfferServices: consentOfferServices,
      consentThirdParty: consentThirdParty,
    };

    try {
      fetch(`${process.env.GATSBY_SEND_EMAIL_URL}`, {
        method: "POST",
        body: JSON.stringify({
          ...data,
          fromAddress: email,
          contactName,
          content: description,
        }),
      });
      onSuccess();
    } catch (error) {
      console.log("error", error);
    }
  };

  const separator = (delay?: number) => (
    <div
      className="contact-form__separator is-animated"
      style={delay && delayStyle(delay)}
    />
  );

  return (
    <form className="contact-form" onSubmit={onSubmit}>
      <div className="contact-form__content">
        <label
          className={`contact-form__label-radio is-animated`}
          style={delayStyle(0)}
        >
          {t("contact.identify")}
        </label>
        <div className="contact-form__radiogroup">
          {userTypes.map((userType, i) => radioButton(userType, i + 1))}
        </div>
        {separator(userTypes.length + 1)}
        <label
          htmlFor="contactName"
          className={`contact-form__label is-animated`}
          style={delayStyle(userTypes.length + 2)}
        >
          <input
            type="text"
            required
            name="contactName"
            placeholder={t("contact.form.namePlaceholder")}
            className={`contact-form__control contact-form__typewriter contact-form__typewriter--start`}
            onChange={(event) => setContactName(event.target.value)}
            style={delayStyle(userTypes.length + 2)}
          />
          {separator(userTypes.length + 3)}
        </label>
        <label htmlFor="email" className={`contact-form__label`}>
          <input
            type="email"
            name="email"
            required
            placeholder={t("contact.form.emailPlaceholder")}
            className="contact-form__control contact-form__control--spaced is-animated"
            style={delayStyle(userTypes.length + 4)}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        {separator(userTypes.length + 5)}
        <label
          htmlFor="description"
          className={`contact-form__label contact-form__label--big`}
        >
          <div
            className="is-animated h-100"
            style={delayStyle(userTypes.length + 6)}
          >
            <textarea
              name="description"
              required
              className="contact-form__control contact-form__control--textarea contact-form__typewriter "
              style={{
                ...textAreaStyles,
                ...delayStyle(userTypes.length + 7),
              }}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("contact.form.descriptionPlaceholder")}
            />
          </div>
        </label>
        {separator(userTypes.length + 8)}
        <div className="contact-form__privacy">
          <label
            className=" is-animated"
            htmlFor="acceptPrivacyPolicy"
            onClick={() => setAcceptPrivacyPolicy(true)}
            style={delayStyle(userTypes.length + 9)}
          >
            <Checkbox name="acceptPrivacyPolicy" isRequired />
            {t("contact.form.policy.privacy")}
            <a href={`/${lang}/aviso-de-confidencialidad`} target="_blank">
              {t("contact.form.policy.privacyLink")}
            </a>
          </label>
          <label
            className=" is-animated"
            htmlFor="acceptConditionsOfUse"
            onClick={() => setAcceptConditionsOfUse(true)}
            style={delayStyle(userTypes.length + 10)}
          >
            <Checkbox name="acceptConditionsOfUse" isRequired />
            {t("contact.form.policy.conditions")}
            <a href={`/${lang}/legal`} target="_blank">
              {t("contact.form.policy.conditionsLink")}
            </a>
          </label>
          <label
            className=" is-animated"
            htmlFor="consentOfferServices"
            onClick={() => setConsentOfferServices(true)}
            style={delayStyle(userTypes.length + 11)}
          >
            <Checkbox name="consentOfferServices" />
            {t("contact.form.policy.consentOfferServices")}
          </label>
          <label
            className=" is-animated"
            htmlFor="consentThirdParty"
            onClick={() => setConsentThirdParty(true)}
            style={delayStyle(userTypes.length + 12)}
          >
            <Checkbox name="consentThirdParty" />
            {t("contact.form.policy.consent")}
          </label>
        </div>
        <div
          className="contact-form__footer is-animated"
          style={delayStyle(userTypes.length + 13)}
        >
          <Button
            type="submit"
            inverse
            uppercase
            isDisabled={!selectedUserType}
          >
            {t("common.submit")}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ContactForm;
